import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { formatClock } from '../MentorAvailabilityPanel';
import MenteePortalNav from './MenteePortalNav';
import MenteeSessionsBookCalendar from './MenteeSessionsBookCalendar';
import MenteeSessionsSlotList from './MenteeSessionsSlotList';
import MenteeSessionsConfirmPanel from './MenteeSessionsConfirmPanel';
import MenteeSessionsUpcoming from './MenteeSessionsUpcoming';
import MenteeSessionsPast from './MenteeSessionsPast';
import MenteeSessionsAgendaModal from './MenteeSessionsAgendaModal';
import MenteeSessionsRateModal from './MenteeSessionsRateModal';
import MenteePortalService, {
    MenteeBookingRow,
    MenteeBookingSlot,
    MenteeCreateBookingBody,
    MenteeLanding,
    MenteePastSessionRow,
} from './MenteePortalService';
import '../mentorDashboard.css';
import './menteeRegistration.css';
import './menteeSessions.css';

type SubTab = 'book' | 'upcoming' | 'past';

const INELIGIBLE_COPY = 'The mentorship program is not available for your campus';

const parseSubTab = (value: string | null): SubTab => {
    if (value === 'upcoming' || value === 'past' || value === 'book') {
        return value;
    }
    return 'book';
};

const istParts = (reference = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(reference);
    const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
    const year = Number(get('year'));
    const month = Number(get('month'));
    return { year, month, key: `${get('year')}-${get('month')}` };
};

const shiftMonth = (year: number, month: number, delta: number) => {
    const d = new Date(Date.UTC(year, month - 1 + delta, 1));
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
};

const monthKey = (year: number, month: number) =>
    `${year}-${String(month).padStart(2, '0')}`;

/**
 * UAS-2382 Sessions — Book / Upcoming / Past (P18-BATCH-006 + 007).
 */
const MenteeSessions = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const initial = istParts();
    const [loading, setLoading] = useState(true);
    const [landing, setLanding] = useState<MenteeLanding | null>(null);
    const [menteeName, setMenteeName] = useState<string | null>(null);
    const [mentorName, setMentorName] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [ineligible, setIneligible] = useState(false);
    const [forbidden, setForbidden] = useState(false);
    const [unpaired, setUnpaired] = useState(false);

    const subTab = parseSubTab(searchParams.get('tab'));
    const setSubTab = (next: SubTab) => {
        setSearchParams({ tab: next }, { replace: true });
    };

    const [year, setYear] = useState(initial.year);
    const [month, setMonth] = useState(initial.month);
    const [slots, setSlots] = useState<MenteeBookingSlot[]>([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slotsError, setSlotsError] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<MenteeBookingSlot | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [bookError, setBookError] = useState<string | null>(null);
    const [lastBooking, setLastBooking] = useState<MenteeBookingRow | null>(null);
    /** When viewing a booked pill, highlight that hour’s booking details. */
    const [focusedBookedStart, setFocusedBookedStart] = useState<number | null>(null);

    const [upcoming, setUpcoming] = useState<MenteeBookingRow[]>([]);
    const [upcomingLoading, setUpcomingLoading] = useState(false);
    const [upcomingError, setUpcomingError] = useState<string | null>(null);

    const [pastSessions, setPastSessions] = useState<MenteePastSessionRow[]>([]);
    const [pastLoading, setPastLoading] = useState(false);
    const [pastError, setPastError] = useState<string | null>(null);

    const [agendaBooking, setAgendaBooking] = useState<MenteeBookingRow | null>(null);
    const [agendaSubmitting, setAgendaSubmitting] = useState(false);
    const [agendaError, setAgendaError] = useState<string | null>(null);

    const [rateSession, setRateSession] = useState<MenteePastSessionRow | null>(null);
    const [rateSubmitting, setRateSubmitting] = useState(false);
    const [rateError, setRateError] = useState<string | null>(null);

    const monthStr = monthKey(year, month);

    const bootstrap = useCallback(() => {
        setLoading(true);
        MenteePortalService.getDashboard()
            .then((dash) => {
                setLanding(dash.landing);
                setIneligible(false);
                setForbidden(false);
                setLoadError(null);
                setMenteeName(dash.identity?.name ?? null);
                if (dash.landing !== 'dashboard') {
                    setUnpaired(false);
                    setMentorName(null);
                    return;
                }
                const mentor = dash.mentor;
                if (!mentor || ('status' in mentor && mentor.status === 'yet_to_be_assigned')) {
                    setUnpaired(true);
                    setMentorName(null);
                    return;
                }
                setUnpaired(false);
                setMentorName(mentor.name || null);
            })
            .catch((err: { status?: number; message?: string }) => {
                const status = err?.status;
                const message = MenteePortalService.errorMessage(err);
                if (status === 403 && /ineligible/i.test(message)) {
                    setIneligible(true);
                    return;
                }
                if (status === 403 || status === 401) {
                    setForbidden(true);
                    return;
                }
                if (status === 404) {
                    setUnpaired(true);
                    setLanding('dashboard');
                    return;
                }
                setLoadError(message);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    const loadUpcoming = useCallback(() => {
        setUpcomingLoading(true);
        setUpcomingError(null);
        MenteePortalService.listUpcomingBookings()
            .then((data) => setUpcoming(data.bookings || []))
            .catch((err: unknown) => {
                setUpcomingError(MenteePortalService.errorMessage(err));
                setUpcoming([]);
            })
            .finally(() => setUpcomingLoading(false));
    }, []);

    const loadPast = useCallback(() => {
        setPastLoading(true);
        setPastError(null);
        MenteePortalService.listPastSessions()
            .then((data) => setPastSessions(data.sessions || []))
            .catch((err: unknown) => {
                setPastError(MenteePortalService.errorMessage(err));
                setPastSessions([]);
            })
            .finally(() => setPastLoading(false));
    }, []);

    useEffect(() => {
        if (landing !== 'dashboard' || unpaired || loading) return;
        // Book tab needs upcoming so “Session booked” can reappear for existing bookings.
        if (subTab === 'book' || subTab === 'upcoming') {
            loadUpcoming();
        }
        if (subTab === 'past') {
            loadPast();
        }
    }, [landing, unpaired, loading, subTab, loadUpcoming, loadPast]);

    const loadSlots = useCallback((ym: string) => {
        setSlotsLoading(true);
        setSlotsError(null);
        MenteePortalService.listBookingSlots(ym)
            .then((data) => {
                setSlots(Array.isArray(data.slots) ? data.slots : []);
            })
            .catch((err: { status?: number; message?: string }) => {
                if (err?.status === 404) {
                    setUnpaired(true);
                    setSlots([]);
                    return;
                }
                setSlotsError(MenteePortalService.errorMessage(err));
                setSlots([]);
            })
            .finally(() => setSlotsLoading(false));
    }, []);

    useEffect(() => {
        if (landing !== 'dashboard' || unpaired || loading || subTab !== 'book') return;
        loadSlots(monthStr);
    }, [landing, unpaired, loading, subTab, monthStr, loadSlots]);

    const noAvailability = useMemo(
        () => !slotsLoading && slots.length === 0 && !slotsError,
        [slotsLoading, slots.length, slotsError]
    );

    const mentorFirst =
        (mentorName || 'Your mentor').trim().split(/\s+/)[0] || 'Your mentor';

    const bookingDateKey = (row: MenteeBookingRow): string =>
        String(row.bookedDate || '').slice(0, 10);

    const bookingsForSelectedDay = useMemo(() => {
        if (!selectedDate) return [] as MenteeBookingRow[];
        return (upcoming || []).filter((b) => bookingDateKey(b) === selectedDate);
    }, [upcoming, selectedDate]);

    /** Prefer just-created booking, else focused hour, else first booking that day. */
    const displayedBooking = useMemo(() => {
        if (
            lastBooking &&
            (!selectedDate || bookingDateKey(lastBooking) === selectedDate)
        ) {
            if (
                focusedBookedStart == null ||
                Number(lastBooking.startMinute || 0) === focusedBookedStart
            ) {
                return lastBooking;
            }
        }
        if (focusedBookedStart != null) {
            const match = bookingsForSelectedDay.find(
                (b) => Number(b.startMinute || 0) === focusedBookedStart
            );
            if (match) return match;
        }
        if (lastBooking && selectedDate && bookingDateKey(lastBooking) === selectedDate) {
            return lastBooking;
        }
        return bookingsForSelectedDay[0] || null;
    }, [lastBooking, selectedDate, focusedBookedStart, bookingsForSelectedDay]);

    const handleSelectDate = (date: string) => {
        setSelectedDate(date);
        setSelectedSlot(null);
        setBookError(null);
        // Keep lastBooking if it belongs to this day; otherwise clear so upcoming drives the card.
        setLastBooking((prev) =>
            prev && bookingDateKey(prev) === date ? prev : null
        );
        const dayBookings = (upcoming || []).filter((b) => bookingDateKey(b) === date);
        const preferred =
            lastBooking && bookingDateKey(lastBooking) === date
                ? Number(lastBooking.startMinute || 0)
                : dayBookings[0]
                  ? Number(dayBookings[0].startMinute || 0)
                  : null;
        setFocusedBookedStart(preferred);
    };

    const handleSelectSlot = (slot: MenteeBookingSlot) => {
        if (slot.state === 'booked') {
            setSelectedSlot(null);
            setBookError(null);
            setFocusedBookedStart(slot.startMinute);
            return;
        }
        if (slot.state !== 'available') return;
        setSelectedSlot(slot);
        setBookError(null);
        setFocusedBookedStart(null);
        setLastBooking(null);
    };

    const handleConfirm = (body: MenteeCreateBookingBody) => {
        setSubmitting(true);
        setBookError(null);
        MenteePortalService.createBooking(body)
            .then((row) => {
                setLastBooking(row);
                setFocusedBookedStart(Number(row.startMinute || body.startMinute));
                setSelectedSlot(null);
                loadSlots(monthStr);
                loadUpcoming();
            })
            .catch((err: { status?: number; message?: string }) => {
                if (err?.status === 409) {
                    setBookError('This slot was just taken. Please pick another.');
                    loadSlots(monthStr);
                    return;
                }
                setBookError(MenteePortalService.errorMessage(err));
            })
            .finally(() => setSubmitting(false));
    };

    const handleSaveAgenda = (agenda: string | null) => {
        if (!agendaBooking) return;
        setAgendaSubmitting(true);
        setAgendaError(null);
        MenteePortalService.patchBookingAgenda(agendaBooking.id, { agenda })
            .then(() => {
                setAgendaBooking(null);
                loadUpcoming();
            })
            .catch((err: unknown) => {
                setAgendaError(MenteePortalService.errorMessage(err));
            })
            .finally(() => setAgendaSubmitting(false));
    };

    const handleSubmitRating = (stars: number, feedbackText: string | null) => {
        if (!rateSession) return;
        setRateSubmitting(true);
        setRateError(null);
        MenteePortalService.createSessionRating(rateSession.id, { stars, feedbackText })
            .then(() => {
                setRateSession(null);
                loadPast();
            })
            .catch((err: unknown) => {
                setRateError(MenteePortalService.errorMessage(err));
            })
            .finally(() => setRateSubmitting(false));
    };

    if (loading && landing === null) {
        return (
            <div className="max-1250 mx-auto px-3 py-4">
                <MenteePortalNav landing={null} />
                <p className="mreg-sub">Loading sessions…</p>
            </div>
        );
    }

    if (ineligible) {
        return (
            <div className="max-1250 mx-auto px-3 py-4">
                <MenteePortalNav landing={null} />
                <p className="mreg-sub">{INELIGIBLE_COPY}</p>
            </div>
        );
    }

    if (forbidden) {
        return (
            <div className="max-1250 mx-auto px-3 py-4">
                <MenteePortalNav landing={null} />
                <p className="mreg-sub">You do not have access to Mentorship.</p>
            </div>
        );
    }

    if (landing && landing !== 'dashboard') {
        return <Navigate to="/alumni/mentorship/mentee/dashboard" replace />;
    }

    if (unpaired) {
        return (
            <div className="max-1250 mx-auto px-3 py-4">
                <MenteePortalNav landing={landing} />
                <h1 className="msess-title">Sessions</h1>
                <p className="msess-empty">
                    You need a published mentor pairing before you can book sessions.
                </p>
            </div>
        );
    }

    return (
        <div className="max-1250 mx-auto px-3 py-4">
            <MenteePortalNav landing={landing} />
            <h1 className="msess-title">Sessions</h1>
            <p className="msess-sub">Book a 1:1 with your mentor, or review upcoming and past sessions.</p>

            <div className="bg-white border border-secondary border-opacity-25 rounded-pill p-1 mb-3 d-inline-flex" role="tablist" aria-label="Sessions sections">
                <button
                    type="button"
                    role="tab"
                    aria-selected={subTab === 'book'}
                    className={`btn h-auto rounded-pill px-4 ${subTab === 'book' ? ' btn-green' : ''}`}
                    onClick={() => setSubTab('book')}
                >
                    <span>Book a Session</span>
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={subTab === 'upcoming'}
                    className={`btn h-auto rounded-pill px-4 ${subTab === 'upcoming' ? ' btn-green' : ''}`}
                    onClick={() => setSubTab('upcoming')}
                >
                    <span>Upcoming</span>
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={subTab === 'past'}
                    className={`btn h-auto rounded-pill px-4 ${subTab === 'past' ? ' btn-green' : ''}`}
                    onClick={() => setSubTab('past')}
                >
                    <span>Past</span>
                </button>
            </div>

            {loadError ? <p className="mreg-banner is-danger">{loadError}</p> : null}

            {subTab === 'upcoming' ? (
                <MenteeSessionsUpcoming
                    bookings={upcoming}
                    menteeName={menteeName}
                    isLoading={upcomingLoading}
                    loadError={upcomingError}
                    onEditAgenda={(booking) => {
                        setAgendaError(null);
                        setAgendaBooking(booking);
                    }}
                />
            ) : null}

            {subTab === 'past' ? (
                <MenteeSessionsPast
                    sessions={pastSessions}
                    isLoading={pastLoading}
                    loadError={pastError}
                    onRate={(session) => {
                        setRateError(null);
                        setRateSession(session);
                    }}
                />
            ) : null}

            {subTab === 'book' ? (
                <>
                    {slotsError ? <p className="mreg-banner is-danger">{slotsError}</p> : null}
                    {slotsLoading ? <p className="mreg-sub">Loading availability…</p> : null}
                    {noAvailability ? (
                        <p className="msess-empty">
                            {mentorFirst} hasn&apos;t set availability yet
                        </p>
                    ) : (
                        <div className="msess-book-layout">
                            <div>
                                <MenteeSessionsBookCalendar
                                    year={year}
                                    month={month}
                                    slots={slots}
                                    selectedDate={selectedDate}
                                    onPrev={() => {
                                        const next = shiftMonth(year, month, -1);
                                        setYear(next.year);
                                        setMonth(next.month);
                                        setSelectedDate(null);
                                        setSelectedSlot(null);
                                        setFocusedBookedStart(null);
                                        setLastBooking(null);
                                    }}
                                    onNext={() => {
                                        const next = shiftMonth(year, month, 1);
                                        setYear(next.year);
                                        setMonth(next.month);
                                        setSelectedDate(null);
                                        setSelectedSlot(null);
                                        setFocusedBookedStart(null);
                                        setLastBooking(null);
                                    }}
                                    onSelectDate={handleSelectDate}
                                />
                                <MenteeSessionsSlotList
                                    date={selectedDate}
                                    slots={slots}
                                    selectedStartMinute={
                                        selectedSlot?.startMinute ?? focusedBookedStart
                                    }
                                    onSelect={handleSelectSlot}
                                />
                            </div>
                            <div>
                                {displayedBooking ? (
                                    <div className="msess-success-card">
                                        <h3>Session booked</h3>
                                        <p>
                                            {displayedBooking.bookedDate} ·{' '}
                                            {formatClock(Number(displayedBooking.startMinute || 0))} ·{' '}
                                            {displayedBooking.durationMinutes} min
                                        </p>
                                        <p>Topic: {displayedBooking.topic}</p>
                                        <p>Mode: {displayedBooking.mode}</p>
                                        <p className="msess-hint">Booking id {displayedBooking.id}</p>
                                    </div>
                                ) : null}
                                {selectedSlot ? (
                                    <MenteeSessionsConfirmPanel
                                        mentorName={mentorName}
                                        slot={selectedSlot}
                                        submitting={submitting}
                                        errorMessage={bookError}
                                        onSubmit={handleConfirm}
                                        onCancel={() => {
                                            setSelectedSlot(null);
                                            setBookError(null);
                                        }}
                                    />
                                ) : !displayedBooking ? (
                                    <aside className="msess-confirm-card">
                                        <h2 className="msess-confirm-title">Confirm booking</h2>
                                        <p className="msess-sub">
                                            Pick an available slot to open the confirmation panel.
                                        </p>
                                    </aside>
                                ) : null}
                            </div>
                        </div>
                    )}
                </>
            ) : null}

            <MenteeSessionsAgendaModal
                show={agendaBooking != null}
                booking={agendaBooking}
                submitting={agendaSubmitting}
                errorMessage={agendaError}
                onClose={() => {
                    setAgendaBooking(null);
                    setAgendaError(null);
                }}
                onSave={handleSaveAgenda}
            />

            <MenteeSessionsRateModal
                show={rateSession != null}
                session={rateSession}
                submitting={rateSubmitting}
                errorMessage={rateError}
                onClose={() => {
                    setRateSession(null);
                    setRateError(null);
                }}
                onSubmit={handleSubmitRating}
            />
        </div>
    );
};

export default MenteeSessions;
